import { facebookService, useGetBusinessID } from '@marketing-milk/frontend'
import { normalizeCallPhoneNumber } from '@marketing-milk/interfaces'
import InputLabel from '@material-ui/core/InputLabel'
import FormHelperText from '@material-ui/core/FormHelperText'
import { allowedCountries } from 'pages/business/lead-cards/wizard/sections/form-completion/CompletionPhone'
import React, { useEffect } from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { useQuery } from 'react-query'

export interface CallPhoneNumberProps {
  /** E.164, or '' while unset. */
  value: string
  onChange(value: string): void
  readOnly: boolean
}

/**
 * The number a Call Now button dials. Every calls ad observed dials the number its Facebook Page
 * lists, so an empty field starts there; the AE can still change it, e.g. to a tracked number.
 */
export function CallPhoneNumber({ value, onChange, readOnly }: CallPhoneNumberProps) {
  const businessID = useGetBusinessID()
  const { data: pagePhone } = useQuery(
    ['facebook-page-phone', businessID],
    () => facebookService.getFacebookPagePhone(businessID),
    // A failed lookup (no Page token, a Graph error) won't succeed on retry, and the field is
    // usable empty either way.
    { staleTime: Infinity, retry: false }
  )

  useEffect(() => {
    if (!value && pagePhone && !readOnly) onChange(pagePhone)
  }, [pagePhone])

  const valid = !!normalizeCallPhoneNumber(value)
  const helperText = !valid
    ? 'Enter the number people reach when they tap Call Now.'
    : pagePhone && value === pagePhone
    ? 'From your Facebook Page. Change it to use a different number, such as a tracked one.'
    : undefined

  return (
    <div className="form-group">
      <InputLabel shrink className="font-semibold" error={!valid}>
        Call Now phone number <span className="text-red">*</span>
      </InputLabel>
      <PhoneInput
        value={value.replace(/^\+/, '')}
        country="us"
        onlyCountries={allowedCountries}
        countryCodeEditable={false}
        disabled={readOnly}
        onChange={digits => onChange(digits ? `+${digits}` : '')}
        inputProps={{
          name: 'call_phone_number',
          'aria-label': 'call phone number',
          required: true,
        }}
        isValid={valid || !value}
      />
      {helperText && <FormHelperText error={!valid}>{helperText}</FormHelperText>}
    </div>
  )
}
